import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { imageUrl } from '../lib/images';
import { useCart } from '../context/CartContext';
import { getCmsSettings } from '../lib/cms';

const DEFAULT_LOGO_URL = 'https://assets.auxodev.dk/assets/e1e86ea9-8aac-4deb-bab2-e96b6ba9dd16/kromix-logo-hvid.png';

const DOG_PRODUCTS = [
  { name: 'Alle hundeprodukter', path: '/hundefoder', tag: 'Katalog' },
  { name: 'KROMIX Fiberkost', path: '/produkter/kromix-fiberkost', tag: 'Aktiv hund' },
  { name: 'KROMIX Naturkost', path: '/produkter/kromix-naturkost', tag: 'Alle racer' },
  { name: 'KROMIX Favorit', path: '/produkter/kromix-favorit', tag: 'Højaktiv' },
  { name: 'KROMIX Lam & Ris', path: '/produkter/kromix-lam-ris', tag: 'Sensitiv' },
  { name: 'KROMIX Minies', path: '/produkter/kromix-minies', tag: 'Små hunde' },
  { name: 'KROMIX Junior', path: '/produkter/kromix-junior', tag: 'Hvalp (2-20 mdr)' },
  { name: 'KROMIX Minikost', path: '/produkter/kromix-minikost', tag: 'Hvalp (normal)' },
];

const DOG_SLUGS = [
  'kromix-fiberkost',
  'kromix-naturkost',
  'kromix-favorit',
  'kromix-lam-ris',
  'kromix-minies',
  'kromix-junior',
  'kromix-minikost',
];

const DIVERSE_SLUGS = [
  'kromix-solsikke',
  'kromix-vildtfugleblanding',
  'kromix-lakseolie',
  'kromix-hvalpemaelk',
  'kromix-knuspies',
  'kromix-loopies',
  'kromix-avant-cat-classic',
  'kromix-avant-cat-exellent',
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dogDropdownOpen, setDogDropdownOpen] = useState(false);
  const [mobileDogOpen, setMobileDogOpen] = useState(false);
  const dropdownRef = useRef(null);
  const closeTimerRef = useRef(null);
  const location = useLocation();
  const { totalItems, totalPrice, openCart } = useCart();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let isMounted = true;
    getCmsSettings().then((data) => {
      if (isMounted && data) setSettings(data);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const logoUrl = settings?.logo?.[0] || DEFAULT_LOGO_URL;
  const telefon = settings?.telefon || '75 73 18 61';
  const email = settings?.email || 'kromix@kromix.dk';
  const virksomhedsnavn = settings?.virksomhedsnavn || 'KROMIX A/S';
  const slogan = settings?.slogan || 'Dansk foderkvalitet siden 1986';

  const handleDogMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setDogDropdownOpen(true);
  };

  const handleDogMouseLeave = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setDogDropdownOpen(false);
    }, 200);
  };

  // Close menus on route change
  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    setMobileMenuOpen(false);
    setDogDropdownOpen(false);
    setMobileDogOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // Click outside to close desktop dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
        }
        setDogDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentProductSlug = location.pathname.startsWith('/produkter/')
    ? location.pathname.replace('/produkter/', '').split('/')[0]
    : '';

  const isDogActive =
    location.pathname.startsWith('/hundefoder') ||
    location.pathname.startsWith('/hund') ||
    DOG_SLUGS.includes(currentProductSlug);

  const isCatActive =
    location.pathname.startsWith('/kat') ||
    currentProductSlug === 'kromix-katteknas';

  const isDiverseActive =
    location.pathname.startsWith('/diverse') ||
    DIVERSE_SLUGS.includes(currentProductSlug);

  return (
    <header className="sticky top-0 z-50 bg-[#39463A] border-b border-[#273228] shadow-sm">
      {/* Top B2B notice & direct contact bar */}
      <div className="bg-[#273228] text-[#FCFAF5] text-xs py-2 px-4 border-b border-[#39463A]/60">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="hidden sm:flex items-center gap-2 font-medium">
            <span className="inline-block w-2 h-2 rounded-full bg-[#B98262] ring-2 ring-[#B98262]/30" />
            <span className="text-[#FCFAF5]/90">{virksomhedsnavn} – {slogan} – Professionel rådgivning</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <a
              href={`tel:${telefon.replace(/\s+/g, '')}`}
              className="inline-flex items-center gap-1.5 text-[#DED3C0] hover:text-[#FCFAF5] transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#BCC6B2]" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium text-[#FCFAF5]">{telefon}</span>
            </a>
            <span className="text-[#BCC6B2]/40">|</span>
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center gap-1.5 text-[#DED3C0] hover:text-[#FCFAF5] transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-[#BCC6B2]" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium text-[#FCFAF5]">{email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0 py-2 group">
            <img
              src={imageUrl(logoUrl, 'width=240,quality=90,format=auto')}
              alt={`${virksomhedsnavn} Logo`}
              className="h-10 sm:h-11 w-auto object-contain transition-transform group-hover:scale-[1.02]"
            />
          </Link>

          {/* Desktop Navigation: Visible ONLY at >= 1200px */}
          <nav className="min-[1200px]:flex max-[1199px]:hidden items-center gap-1 xl:gap-2">
            {/* Hundefoder with dropdown */}
            <div
              className="relative py-2"
              ref={dropdownRef}
              onMouseEnter={handleDogMouseEnter}
              onMouseLeave={handleDogMouseLeave}
            >
              <NavLink
                to="/hundefoder"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${
                    isActive || isDogActive
                      ? 'text-white bg-[#273228] shadow-inner ring-1 ring-[#71806A]/40'
                      : 'text-[#FCFAF5]/85 hover:text-white hover:bg-[#273228]/50'
                  }`
                }
              >
                <span>Hundefoder</span>
                <i
                  className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                    dogDropdownOpen ? 'rotate-180 text-[#BCC6B2]' : 'text-[#DED3C0]'
                  }`}
                  aria-hidden="true"
                />
              </NavLink>

              {/* Dropdown Menu */}
              {dogDropdownOpen && (
                <div
                  className="absolute left-0 top-full pt-1 w-72 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  onMouseEnter={handleDogMouseEnter}
                  onMouseLeave={handleDogMouseLeave}
                >
                  <div className="rounded-2xl bg-[#FCFAF5] shadow-2xl border border-[#DED3C0] p-2 py-2.5">
                    <div className="px-3 py-1.5 mb-1 border-b border-[#DED3C0] text-[11px] font-bold uppercase tracking-wider text-[#71806A]">
                      Hundefoder Sortiment
                    </div>
                    {DOG_PRODUCTS.map((prod) => (
                      <Link
                        key={prod.path}
                        to={prod.path}
                        className="group flex items-center justify-between px-3 py-2 text-xs font-medium rounded-lg text-[#39463A] hover:bg-[#F4EFE5] hover:text-[#39463A] transition-colors"
                      >
                        <span className="font-semibold">{prod.name}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#BCC6B2]/30 text-[#39463A] group-hover:bg-[#71806A] group-hover:text-[#FCFAF5] transition-colors">
                          {prod.tag}
                        </span>
                      </Link>
                    ))}
                    <div className="mt-2 pt-2 border-t border-[#DED3C0] px-3">
                      <Link
                        to="/hundefoder"
                        className="text-xs font-bold text-[#71806A] hover:text-[#39463A] flex items-center gap-1.5 transition-colors"
                      >
                        <span>Se hele oversigten</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Kattefoder */}
            <NavLink
              to="/kat"
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive || isCatActive
                    ? 'text-white bg-[#273228] shadow-inner ring-1 ring-[#71806A]/40'
                    : 'text-[#FCFAF5]/85 hover:text-white hover:bg-[#273228]/50'
                }`
              }
            >
              Kattefoder
            </NavLink>

            {/* Tilbehør & Diverse */}
            <NavLink
              to="/diverse"
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive || isDiverseActive
                    ? 'text-white bg-[#273228] shadow-inner ring-1 ring-[#71806A]/40'
                    : 'text-[#FCFAF5]/85 hover:text-white hover:bg-[#273228]/50'
                }`
              }
            >
              Tilbehør
            </NavLink>

            {/* Josera */}
            <NavLink
              to="/josera"
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive
                    ? 'text-white bg-[#273228] shadow-inner ring-1 ring-[#71806A]/40'
                    : 'text-[#FCFAF5]/85 hover:text-white hover:bg-[#273228]/50'
                }`
              }
            >
              Josera
            </NavLink>

            {/* Om KROMIX */}
            <NavLink
              to="/om-kromix"
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive
                    ? 'text-white bg-[#273228] shadow-inner ring-1 ring-[#71806A]/40'
                    : 'text-[#FCFAF5]/85 hover:text-white hover:bg-[#273228]/50'
                }`
              }
            >
              Om os
            </NavLink>

            {/* Kontakt */}
            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive
                    ? 'text-white bg-[#273228] shadow-inner ring-1 ring-[#71806A]/40'
                    : 'text-[#FCFAF5]/85 hover:text-white hover:bg-[#273228]/50'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Desktop Right CTA Button: Sage Olive primary action & Webshop Cart */}
          <div className="min-[1200px]:flex max-[1199px]:hidden items-center gap-2.5">
            {/* Cart Trigger */}
            <button
              type="button"
              onClick={openCart}
              className="relative inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-[#273228] hover:bg-[#1E261F] text-white text-xs font-bold transition-all border border-[#71806A]/30 shadow-xs cursor-pointer group"
              aria-label={`Åbn bestillingskurv (${totalItems} varer)`}
            >
              <i className="fa-solid fa-basket-shopping text-sm text-[#BCC6B2] group-hover:text-white transition-colors" />
              <span>Kurv</span>
              {totalItems > 0 ? (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#B98262] text-white text-[10px] font-extrabold flex items-center justify-center">
                  {totalItems}
                </span>
              ) : null}
            </button>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white text-xs font-bold uppercase tracking-wider transition-all shadow-sm hover:shadow-md active:scale-98 whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Kontakt os</span>
            </Link>
          </div>

          {/* Mobile Right Controls: Cart & Burger */}
          <div className="min-[1200px]:hidden flex items-center gap-1.5">
            {/* Mobile Cart Button */}
            <button
              type="button"
              onClick={openCart}
              className="relative p-2.5 rounded-lg text-[#FCFAF5] hover:bg-[#273228] transition-colors cursor-pointer"
              aria-label={`Åbn bestillingskurv med ${totalItems} varer`}
            >
              <i className="fa-solid fa-basket-shopping text-xl" />
              {totalItems > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#B98262] text-white text-[10px] font-extrabold flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>

            {/* Burger menu button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-lg text-[#FCFAF5] hover:bg-[#273228] transition-colors focus:outline-hidden"
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile navigation drawer (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-[#273228] bg-[#39463A] px-4 pt-3 pb-8 space-y-2 shadow-2xl animate-in slide-in-from-top-2 text-[#FCFAF5] max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain">
          {/* Hundefoder Accordion */}
          <div>
            <div className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-[#273228]">
              <Link
                to="/hundefoder"
                onClick={() => setMobileMenuOpen(false)}
                className="text-base font-bold text-white"
              >
                Hundefoder
              </Link>
              <button
                type="button"
                onClick={() => setMobileDogOpen(!mobileDogOpen)}
                className="p-1 text-[#DED3C0]"
                aria-label="Fold hundefoder ud"
              >
                <i
                  className={`fa-solid fa-chevron-down transition-transform ${mobileDogOpen ? 'rotate-180 text-[#BCC6B2]' : ''}`}
                  aria-hidden="true"
                />
              </button>
            </div>
            {mobileDogOpen && (
              <div className="pl-4 pr-2 py-1 space-y-1 bg-[#273228]/80 rounded-xl mt-1 border border-[#71806A]/20">
                {DOG_PRODUCTS.map((prod) => (
                  <Link
                    key={prod.path}
                    to={prod.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-2 px-2.5 text-xs font-medium text-[#FCFAF5]/90 hover:text-white hover:bg-[#39463A] rounded-lg"
                  >
                    <span>{prod.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#71806A]/30 text-[#DED3C0]">{prod.tag}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/kat"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-3 py-2.5 rounded-lg text-base font-semibold text-[#FCFAF5]/90 hover:bg-[#273228]"
          >
            Kattefoder
          </Link>

          <Link
            to="/diverse"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-3 py-2.5 rounded-lg text-base font-semibold text-[#FCFAF5]/90 hover:bg-[#273228]"
          >
            Tilbehør & Diverse
          </Link>

          <Link
            to="/josera"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-3 py-2.5 rounded-lg text-base font-semibold text-[#FCFAF5]/90 hover:bg-[#273228]"
          >
            Josera
          </Link>

          <Link
            to="/om-kromix"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-3 py-2.5 rounded-lg text-base font-semibold text-[#FCFAF5]/90 hover:bg-[#273228]"
          >
            Om KROMIX
          </Link>

          <Link
            to="/kontakt"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-3 py-2.5 rounded-lg text-base font-semibold text-[#FCFAF5]/90 hover:bg-[#273228]"
          >
            Kontakt
          </Link>

          {/* Mobile Cart Link */}
          <button
            type="button"
            onClick={() => {
              setMobileMenuOpen(false);
              openCart();
            }}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-base font-bold text-white bg-[#273228] hover:bg-[#1E261F] transition-colors mt-2"
          >
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-basket-shopping text-[#BCC6B2]" />
              <span>Bestillingskurv (Webshop)</span>
            </span>
            <span className="px-2 py-0.5 rounded-full bg-[#B98262] text-xs font-bold">
              {totalItems} varer
            </span>
          </button>

          <div className="pt-4 mt-3 border-t border-[#273228] flex flex-col gap-3">
            <Link
              to="/kontakt"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full text-center py-3.5 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white font-bold text-sm uppercase tracking-wider transition-colors shadow-md"
            >
              Kontakt KROMIX
            </Link>
            <div className="flex justify-around text-xs text-[#DED3C0] pt-1">
              <a href="tel:75731861" className="inline-flex items-center gap-1.5 font-medium whitespace-nowrap hover:text-white">
                <i className="fa-solid fa-phone text-[#71806A]" aria-hidden="true" />
                <span className="whitespace-nowrap">75 73 18 61</span>
              </a>
              <a href="mailto:kromix@kromix.dk" className="inline-flex items-center gap-1.5 font-medium whitespace-nowrap hover:text-white">
                <i className="fa-solid fa-envelope text-[#71806A]" aria-hidden="true" />
                <span className="whitespace-nowrap">kromix@kromix.dk</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
